import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, PlayCircle } from 'lucide-react';


const SLIDE_DATA = [
  {
    id: 1,
    title: "Bienvenido al Futuro del Deporte",
    subtitle: "Una experiencia única te espera",
    buttonText: "Registrate Ahora",
    // A placeholder video URL
    videoUrl: "https://videos.pexels.com/video-files/4107888/4107888-uhd_2560_1440_25fps.mp4",
    imageUrl: "https://images.pexels.com/photos/3209828/pexels-photo-3209828.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  },
  {
    id: 2,
    title: "Patrocinios Elite",
    subtitle: "Oportunidades de negocio",
    buttonText: "Conocer más",
    imageUrl: "https://images.unsplash.com/photo-1557174949-3b1f5b2e8fac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhbWVyaWNhbiUyMGZvb3RiYWxsJTIwc3RhZGl1bSUyMGNyb3dkfGVufDF8fHx8MTc1NTE1MTkwMXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  },
  {
    id: 3,
    title: "GESTIÓN TOTAL",
    subtitle: "Administración profesional. Herramientas completas para tu equipo",
    buttonText: "Conocer más",
    imageUrl: "https://images.pexels.com/photos/38568/apple-imac-ipad-workplace-38568.jpeg",
  },
  {
    id: 4,
    title: "ESTADÍSTICAS AVANZADAS",
    subtitle: "Análisis en tiempo real. Métricas profesionales para cada jugada",
    buttonText: "Conocer más",
    imageUrl: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  },
  {
    id: 5,
    title: "Contenido Premium",
    subtitle: "Fotos y videos exclusivos",
    buttonText: "Discover Art",
    imageUrl: "https://images.pexels.com/photos/3797928/pexels-photo-3797928.jpeg",
  },
];

const SWIPE_CONFIDENCE_THRESHOLD = 10000;

// --- Animation Variants ---
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const textContainerVariants = {
  hidden: { opacity: 0 },
  visible: (i = 1) => ({
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: i * 0.2 },
  }),
};

const textItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { damping: 15, stiffness: 100 },
  },
};

const HeroSlider = () => {
  const [[page, direction], setPage] = useState([0, 0]);

  // Wrap the index to loop through slides
  const activeIndex = page < 0 ? SLIDE_DATA.length + (page % SLIDE_DATA.length) : page % SLIDE_DATA.length;
  const slide = SLIDE_DATA[activeIndex];

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      paginate(1);
    }, 8000); // Change slide every 8 seconds
    return () => clearInterval(interval);
  }, [page]);

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden flex justify-center items-center">
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={page}
          className="absolute w-full h-full"
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = Math.abs(offset.x) * velocity.x;
            if (swipe < -SWIPE_CONFIDENCE_THRESHOLD) {
              paginate(1);
            } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD) {
              paginate(-1);
            }
          }}
        >
          {/* Background Video */}
          {slide.videoUrl && (
            <video
              src={slide.videoUrl}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover z-0"
              onError={(e) => console.error("Video Error:", e)}
            />
          )}
          {/* Background Image (fallback) */}
          <img
            src={slide.imageUrl}
            alt={slide.title}
            className="absolute inset-0 w-full h-full object-cover z-0"
            style={{ opacity: slide.videoUrl ? 0 : 1 }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/50 z-10" />

          {/* Content */}
          <motion.div
            className="relative z-20 flex flex-col justify-center items-center h-full text-white p-8"
            variants={textContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h1
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-center mb-4 leading-tight tracking-tight"
              variants={textItemVariants}
            >
              {slide.title}
            </motion.h1>
            <motion.p
              className="text-base md:text-xl text-center max-w-2xl mb-8"
              variants={textItemVariants}
            >
              {slide.subtitle}
            </motion.p>
            <motion.button
              className="bg-white text-black font-bold py-3 px-8 rounded-full flex items-center gap-2 text-lg"
              variants={textItemVariants}
              whileHover={{ scale: 1.05, boxShadow: "0px 0px 15px rgba(255,255,255,0.3)" }}
              whileTap={{ scale: 0.95 }}
            >
              <PlayCircle size={22} />
              {slide.buttonText}
            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <button onClick={() => paginate(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 text-white p-3 rounded-full backdrop-blur-sm hover:bg-white/30 transition-colors">
        <ChevronLeft size={28} />
      </button>
      <button onClick={() => paginate(1)} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 text-white p-3 rounded-full backdrop-blur-sm hover:bg-white/30 transition-colors">
        <ChevronRight size={28} />
      </button>

      {/* Pagination Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
        {SLIDE_DATA.map((_, i) => (
          <button
            key={i}
            onClick={() => setPage([i, i > activeIndex ? 1 : -1])}
            className={`w-3 h-3 rounded-full transition-colors ${i === activeIndex ? 'bg-white' : 'bg-white/40'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;